import { v4 as uuid } from "uuid";

import { intersection, orderBy } from "lodash";
import { format, parseISO } from "date-fns";
import { formatInTimeZone, format as formatTz } from "date-fns-tz";
import { DropdownItem } from "../shared-components/DropdownCombo";
import { useEffect, useState } from "react";
import { ENV } from "../GeneralConfig";

export const cleanString = (str: string = "") =>
  str.replace(/\s/g, "").toLowerCase();

export const showDateUTCMMDD = (day: Date) => {
  return new Date(day).toLocaleDateString("en", {
    month: "numeric",
    day: "numeric",
    timeZone: "UTC",
  });
};

// Maximum number of days in the past allowed for workflow status queries
export const MAX_WORKFLOW_STATUS_DAYS_PAST = 10;

// Validate and adjust workflow status date if needed
export const validateWorkflowStatusDate = (
  date: string
): {
  adjustedDate: string;
  wasAdjusted: boolean;
} => {
  if (date === "0") {
    return { adjustedDate: date, wasAdjusted: false };
  }

  const inputDate = new Date(date);
  const now = new Date();
  const minAllowedDate = new Date();
  minAllowedDate.setDate(now.getDate() - MAX_WORKFLOW_STATUS_DAYS_PAST);
  minAllowedDate.setHours(0, 0, 0, 0);

  // Only adjust dates that are MORE than MAX_WORKFLOW_STATUS_DAYS_PAST days in the past
  // This means exactly 5 days ago is allowed, but 6 or more days ago will be adjusted
  minAllowedDate.setDate(minAllowedDate.getDate() - 1); // Subtract one more day to make the comparison exclusive
  if (inputDate < minAllowedDate) {
    // When adjusting, set to exactly MAX_WORKFLOW_STATUS_DAYS_PAST days ago
    const adjustDate = new Date();
    adjustDate.setDate(now.getDate() - MAX_WORKFLOW_STATUS_DAYS_PAST);
    adjustDate.setHours(0, 0, 0, 0);
    const adjustedDate = adjustDate.toISOString().split("T")[0];
    return { adjustedDate, wasAdjusted: true };
  }

  return { adjustedDate: date, wasAdjusted: false };
};

export const getMinDueDateFilter = (date: string) => {
  const start = new Date(date);
  start.setUTCHours(0, 0, 0, 0);
  return start.toISOString();
};

export const getMaxDueDateFilter = (date: string) => {
  let end = new Date(date);
  if (date === "0") {
    return formatInTimeZone(Date.now(), "UTC", `yyyy-MM-dd'T'HH:mm:ss'Z'`);
  }

  end.setUTCHours(23, 59, 59, 999);
  return end.toISOString();
};

export function classNames(
  ...classes: Array<string | false | null | undefined>
) {
  return classes.filter(Boolean).join(" ");
}

/** User names, note authors, and similar strings in sidebars and flex layouts */
export const LONG_TEXT_WRAP_CLASSES =
  "min-w-0 max-w-full break-words [overflow-wrap:anywhere]";

export const formattedDate = (
  str: string | Date | null | undefined
): string => {
  try {
    if (null === str || str === undefined) return "NA";
    const date: Date = typeof str === "string" ? parseISO(str) : str;
    return formatTz(date, "MMM dd, yyyy");
  } catch (e) {
    return "NA";
  }
};

export const formattedDateLong = (str: string | Date | null) => {
  if (null === str) return "NA";
  const date: Date = typeof str === "string" ? parseISO(str) : str;
  return format(date, "MMM dd, yyyy HH:mm a z");
};

export const formattedDateLongTz = (str: string | Date | null) => {
  if (null === str) return "NA";
  const date: Date = typeof str === "string" ? parseISO(str) : str;
  return formatTz(date, "MMM dd, yyyy HH:mmz");
};

export const formattedTimeFull = (str: string | Date | null) => {
  if (null === str) return "NA";
  const date: Date = typeof str === "string" ? parseISO(str) : str;
  return formatTz(date, "HH:mm");
};

export const formattedISODate = (str: string | Date | null) => {
  if (null === str) return "NA";
  const date: Date = typeof str === "string" ? parseISO(str) : str;
  return format(date, "yyyy-MM-dd");
};

export const formattedShortTime = (str: string | Date | null) => {
  if (null === str) return "NA";
  const date: Date = typeof str === "string" ? parseISO(str) : str;
  return formatTz(date, "yyyy-MM-dd'\n'HH:mm z");
};

export const formattedLongUTC = (str: string | Date | null) => {
  if (null === str) return "NA";
  const date: Date = typeof str === "string" ? parseISO(str) : str;
  return formatInTimeZone(date, "UTC", `yyyy-MM-dd'\n'HH:mm:ss z`);
};

const HAS_TIMEZONE_SUFFIX = /(?:Z|[+-]\d{2}:\d{2})$/;

export const ensureUtcIso = (str: string): string => {
  if (HAS_TIMEZONE_SUFFIX.test(str)) return str;
  return `${str}Z`;
};

const parseDateAsUtc = (str: string | Date | null): Date | null => {
  if (null === str) return null;
  if (typeof str === "string") {
    return parseISO(ensureUtcIso(str));
  }
  return str;
};

export const formattedISOUTC = (str: string | Date | null) => {
  if (null === str) return "NA";
  const date: Date = typeof str === "string" ? parseISO(str) : str;
  return formatInTimeZone(date, "UTC", `yyyy-MM-dd'T'HH:mm:ss'Z'`);
};

export const ISOToMilliseconds = (str: string | Date | null) => {
  if (null === str) return 0;
  const date: Date = typeof str === "string" ? parseISO(str) : str;
  return new Date(date).getTime();
};

function formattedDateByType(
  date: string | Date | null,
  dateFormatType: "short" | "long" | "longUTC" | "longForceUTCToLocalTime"
) {
  if (dateFormatType === "short") {
    return formattedShortTime(date);
  }
  if (dateFormatType === "long") {
    return formattedDateLongTz(date);
  }
  if (dateFormatType === "longUTC") {
    return formattedLongUTC(date);
  }
  if (dateFormatType === "longForceUTCToLocalTime") {
    return formattedDateLongTz(parseDateAsUtc(date));
  }
  return "";
}

export const dateWithAsterisk = (
  date: string | Date,
  updated: boolean,
  dateFormatType:
    | "short"
    | "long"
    | "longUTC"
    | "longForceUTCToLocalTime" = "short"
) => {
  let formattedDate = formattedDateByType(date, dateFormatType);

  return (
    <>
      {!updated ? (
        formattedDate
      ) : (
        <span>
          {formattedDate}
          <span className="text-red-600 text-2xl font-bold">*</span>
        </span>
      )}
    </>
  );
};

export const FormattedDateWithAsterisk = (props: {
  date: string;
  updated: boolean;
  dateFormatType: "short" | "long" | "longUTC";
  isUnderlined?: boolean;
}) => {
  const { date, updated, dateFormatType, isUnderlined } = props;
  let formattedDate = formattedDateByType(date, dateFormatType);
  if (updated) {
    return (
      <div className="inline-flex items-center gap-2">
        <span className={isUnderlined ? "underline" : ""}>{formattedDate}</span>
        <span className=" text-red-600 text-2xl font-bold flex items-center justify-center pt-1.5">
          *
        </span>
      </div>
    );
  }
  return <span>{formattedDate}</span>;
};

export const formattedShortestTime = (str: string | Date | null) => {
  if (null === str) return "NA";
  const date: Date = typeof str === "string" ? parseISO(str) : str;
  return formatTz(date, "M/d");
};

//Format date to ISO without timezone for endpoint
export function formatDateForEndpoint(dateString: string | undefined) {
  try {
    if (!dateString) return;
    const date = new Date(dateString);
    if (date.toString() === "Invalid Date") {
      return "";
    }
    return date.toISOString().split("T")[0];
  } catch (e) {
    return dateString;
  }
}

export function uniqueUUID() {
  return uuid();
}

export function checkForPermissionInList(
  fullListOfPermissions: string[],
  permissionsToCheck: string[]
): boolean {
  const result = intersection(fullListOfPermissions, permissionsToCheck);
  return result.length > 0;
}

export const validURL = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch (err) {
    return false;
  }
};

export const imageTypeInURL = (str: string): boolean => {
  var pattern = new RegExp(
    "^.*(jpeg|jpg|gif|png|jfif|tiff|bmp|ppm|pgm|pbm|pnm|hdr|heif|bat|bpg|svg).*$",
    "i"
  ); // fragment locator
  return !!pattern.test(str);
};

export const resolveAndDownload = (reportUrl: string) => {
  if (!reportUrl) {
    throw new Error(`PDF creation response was malformed`);
  }
  const link = document.createElement("a");
  link.download = "true";
  link.target = "_blank";
  link.href = reportUrl;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/** Copy text to clipboard; falls back to execCommand if clipboard API unavailable. */
export const copyToClipboard = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  }
};

export const FAMA_SUPPORT_EMAIL =
  process.env.SUPPORT_EMAIL || "support@fama.io";

export const getSocialMediaFromUrl = (url: string): string => {
  const KEYS_REGEX = new RegExp(
    "(?:https?:\\/\\/(?:[a-z0-9-]*.)?)?(facebook|instagram|reddit|twitter|x|tiktok|linkedin).*"
  );

  let socialMedia = url.replace(KEYS_REGEX, "$1");

  if (socialMedia === url)
    throw new Error("Unable to determine social media from " + url);

  socialMedia = socialMedia.charAt(0).toUpperCase() + socialMedia.slice(1);
  return socialMedia;
};

export const dropdownItemSortEval = (a: DropdownItem, b: DropdownItem) => {
  const labelA = a.label.toLowerCase();
  const labelB = b.label.toLocaleLowerCase();
  if (labelA === labelB) return 0;
  else if (labelA < labelB) return -1;
  return 1;
};

export function sortNamedListAlpha(list: any[], descending?: boolean) {
  const order = descending ? "desc" : "asc";

  return orderBy(
    list,
    [
      (element) => {
        if (typeof element === "string") return element;
        if (element.name) return element.name.toLowerCase();
      },
    ],
    [order]
  );
}

export const getBehaviors = (): [string, string][] => {
  const initialBehaviors: [string, string][] = [
    [
      "HARASSMENT",
      "Online harassment and content that bullies, intimidates, harasses, ostracizes, shames, name-calls, or insults a person.",
    ],
    [
      "INTOLERANCE",
      "Prejudice on the basis of characteristics such as race, gender, sex, sexual orientation, and national origin; use of bigoted symbols and images.",
    ],
    [
      "SEX",
      "Sexual innuendos, nude photos, explicit or suggestive imagery, adult sex stores, pornographic content.",
    ],
    [
      "CANNABIS",
      "Use, growth or sale of cannabis (including CBD products), images of cannabis in any form.",
    ],
    [
      "COMMUNITY",
      "Identify mentions of donations, charity, and volunteering that are not related to the Armed services.",
    ],
    [
      "CRIMINAL",
      "Involvement in crimes such as theft or terrorism; arrest records, gang affiliations, support of criminal activity.",
    ],
    [
      "OTHER DRUGS",
      "References to or consumption of illicit drugs, use of drugs outside of their intended purpose",
    ],
    [
      "THREAT",
      "Desire to physically hurt oneself or others, intent to destroy property, control, and intimidation.",
    ],
    [
      "VIOLENCE",
      "Behavior involving physical force intended to hurt, damage, or kill someone or something.",
    ],
  ];
  return [...initialBehaviors].sort((a, b) => a[0].localeCompare(b[0]));
};

export function useDebounce<T>(value: T, delay?: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay || 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

const checkTypeColors: { [key: string]: string } = {
  // DO NOT EXPORT KEEP PRIVATE
  "": "text-black",
  Default: "text-black",
  Green: "text-emerald-400",
  Orange: "text-orange-400",
  Blue: "text-blue-600",
  Pink: "text-pink-500",
};

const checkTypeBackgroundColors: { [key: string]: string } = {
  "": "bg-black",
  Default: "bg-black",
  Green: "bg-emerald-400",
  Orange: "bg-orange-400",
  Blue: "bg-blue-600",
  Pink: "bg-pink-500",
};

export const getColorFromCheckType = (color: string | null) => {
  return checkTypeColors[color ?? ""] ?? checkTypeColors.Default;
};

export const getBackgroundColorFromCheckType = (color: string | null) => {
  return (
    checkTypeBackgroundColors[color ?? ""] ?? checkTypeBackgroundColors.Default
  );
};

export const checkTypeColorItems: DropdownItem[] = [
  { id: "Default", label: "Default - Black" },
  { id: "Green", label: "Green" },
  { id: "Blue", label: "Blue" },
  { id: "Orange", label: "Orange" },
  { id: "Pink", label: "Pink" },
];

export function atLeastOneItem<T>(list: T[] | undefined): boolean {
  return !!(list && list.length > 0);
}

const getTimeDifferenceInMinutes = (str: string): number | string => {
  try {
    if (null === str || (typeof str === "string" && str.length === 0))
      return "";

    const date: Date = typeof str === "string" ? parseISO(str + "Z") : str;

    const currentTime = new Date();
    const activityTime = new Date(date);
    return (currentTime.getTime() - activityTime.getTime()) / (1000 * 60);
  } catch (err) {
    return "";
  }
};

const MAX_TIME_LAST_ACTIVITY_MIN = 360;
const MIN_TIME_LAST_ACTIVITY_MIN = 45;

function checkWhichTimeBucketByTimeDifferenceBetween(
  timeDifference: string | number
): boolean {
  return (
    typeof timeDifference === "number" &&
    timeDifference < MAX_TIME_LAST_ACTIVITY_MIN &&
    timeDifference > MIN_TIME_LAST_ACTIVITY_MIN
  );
}

function checkWhichTimeBucketByTimeDifferenceMin(
  timeDifference: string | number
): boolean {
  return (
    typeof timeDifference === "number" &&
    timeDifference < MIN_TIME_LAST_ACTIVITY_MIN
  );
}

export const formattedLastActivity = (str: string): string => {
  const timeDifference = getTimeDifferenceInMinutes(str);

  if (checkWhichTimeBucketByTimeDifferenceBetween(timeDifference)) {
    return ">45 min";
  } else if (checkWhichTimeBucketByTimeDifferenceMin(timeDifference)) {
    return formattedShortTime(parseISO(str + "Z")); // We force date times from DB to be UTC
  }

  return "";
};

export const formattedLastActivityDateBetween = (str: string): string => {
  const timeDifference = getTimeDifferenceInMinutes(str);

  if (checkWhichTimeBucketByTimeDifferenceBetween(timeDifference)) {
    return ">45";
  } else if (checkWhichTimeBucketByTimeDifferenceMin(timeDifference)) {
    return "<45";
  }

  return "";
};

export const initializeLogging = (
  logMessage: (message: string, details?: any) => void
) => {
  console.error = _wrapConsoleMethod(console.error, logMessage);
  console.warn = _wrapConsoleMethod(console.warn, logMessage);
  console.log = _wrapConsoleMethod(console.log, logMessage); // eslint-disable-line no-console
  console.debug = _wrapConsoleMethod(console.debug, logMessage); // eslint-disable-line no-console
};

export const processNetworkLog = (
  error: any,
  logger: (X: string, y: any) => void
): any => {
  const { method, url } = error.config ? error.config : ["NA", "NA"];
  const data = error.response && error.response.data ? error.response.data : {};
  logger(error.message, { method, url, data });
  error.__logged = true;
  return error;
};

const _wrapConsoleMethod = (
  originalMethod: Function,
  logMessage: (message: string, details?: any) => void
) => {
  return (...args: any[]) => {
    if (
      args.length > 0 &&
      args[0] instanceof Error &&
      (args[0] as any).__logged
    ) {
      return;
    }
    if (ENV !== "local") {
      logMessage(args.join(" "));
    }
    originalMethod.apply(console, args);
  };
};

export function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const blueprint_tooltip = `Business Day Calculation:<br />
Reports submitted by 10am PT / 1pm ET on a business day will be completed by the next business day at 2pm PT / 5pm ET. This applies to all candidates submitted via our web portal and API.<br />
<br />
For reports submitted after 10am PT / 1pm ET on a business day, the report will be completed before 2pm PT / 5pm ET on the second business day.<br />
<br />
For any requests submitted over the weekend, these will be treated as if they were received first thing on Monday morning. Clients can expect reports back the following Tuesday by 2pm PT / 5pm ET.<br />
<br />
360 Report Standard SLA:<br />
Reports submitted by 10am PT / 1pm ET on a business day will be completed by the third business day at 5 pm PT / 8 pm ET. This applies to all candidates submitted via our web portal and API.<br />
<br />
For reports submitted after 10am PT / 1pm ET on a business day, the report will be completed before 5 pm PT / 8 pm ET on the fourth business day.<br />
<br />
For any requests submitted over the weekend, these will be treated as if they were received first thing on Monday morning. Clients can expect reports back the following Thursday by 5 pm PT / 8 pm ET.<br />
<br />
SLA Calculation:<br />
Reports due in X hours after submission.<br />`;
